<template>
  <div class="copy-assets-modal">
    <b-modal
      id="copy-assets-modal"
      no-close-on-backdrop
      :title="getModalTitle()"
      :size="isImageSaveMode ? 'lg' : 'xl'"
      centered
      scrollable
      :modal-class="modalClass"
      :body-class="isImageSaveMode ? 'copy-assets-modal__body' : ''"
      @shown="handleModalShown"
      @hide="handleModalHide"
    >
      <div
        v-if="isImageSaveMode"
        class="copy-assets-modal__filename mb-3"
      >
        <label
          for="copy-assets-save-filename"
          class="copy-assets-modal__filename-label"
        >
          File name
        </label>
        <b-form-input
          id="copy-assets-save-filename"
          v-model.trim="saveFilename"
          placeholder="Enter file name"
          :disabled="isCopying"
        />
      </div>
      <CopyAssetsDestinationView
        ref="destinationView"
        :current-folder-id="currentFolderId"
        :current-tab="currentTab"
        :is-save-mode="localMode === 'save'"
        @folder-selected="onFolderSelected"
      />

      <template #modal-footer="{ hide }">
        <div class="d-flex justify-content-end w-100">
          <b-button
            class="mr-2"
            variant="secondary"
            @click="hide()"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="isActionDisabled() || isCopying"
            @click="handleAction"
          >
            <span v-if="isCopying">
              <b-spinner
                small
                class="mr-1"
              />
              {{ getLoadingText() }}...
            </span>
            <span v-else>{{ getActionButtonText() }}</span>
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import { createNamespacedHelpers } from 'vuex';
import CopyAssetsDestinationView from './CopyAssetsDestinationView.vue';
import { TABS } from '../constants';
import { saveSelectionAsEventBroker, EVENTS as SAVE_SELECTION_EVENTS } from '@frontend/group/modules/save-selection-as/event-broker';
import { backgroundThemeHandlerEventBroker, COPY_TO_FILE_BROWSER } from '@frontend/group/modules/background-theme-color/button-handler';
import { videoEventBroker, VIDEO_EVENTS } from '@frontend/group/modules/video/event-broker';

const { mapGetters } = createNamespacedHelpers('fileBrowser');

export default {
  name: 'CopyAssetsModal',

  components: {
    CopyAssetsDestinationView
  },

  data() {
    return {
      currentFolderId: null,
      selectedFolder: null,
      isCopying: false,
      currentTab: null,
      copyUrl: null,
      TABS,
      localMode: 'copy',
      localMediaData: null,
      localThemeAsset: null,
      saveFilename: '',
    }
  },

  computed: {
    ...mapGetters({
      selectedItemsFromStore: 'selectedItems'
    }),

    selectedItems() {
      if (this.localMode === 'save') {
        return [];
      }

      if (this.localMode === 'copy' && this.localThemeAsset) {
        const themeAssetArray = [this.localThemeAsset];

        return themeAssetArray;
      }

      return this.selectedItemsFromStore || [];
    },

    isImageSaveMode() {
      return this.localMode === 'save' && this.currentTab !== TABS.VIDEO;
    },

    modalClass() {
      const classes = [];

      if (this.localMode === 'save') {
        classes.push('save-image-modal-high-z', 'select-folder-modal');
      }

      return classes.join(' ');
    },
  },

  mounted() {
    if (this.localMode === 'save') {
      this.currentTab = TABS.FILES;
    } else {
      this.currentTab = this.$route?.params?.tab || TABS.BYNDER_ASSETS;
    }
    this.setCopyUrl(this.currentTab);

    saveSelectionAsEventBroker.on(SAVE_SELECTION_EVENTS.SAVE_PROCESSED_IMAGE_TO_FILE_BROWSER, this.initializeSaveMode);
    backgroundThemeHandlerEventBroker.on(COPY_TO_FILE_BROWSER, this.initializeThemeCopyMode);
    videoEventBroker.on(VIDEO_EVENTS.SHOW_FOLDERS_MODAL, this.initializeVideoSaveMode);

    this.handleModalZIndex = (_, modalId) => {
      if (modalId === 'copy-assets-modal') {
        this.$nextTick(() => {
          const modalOuter = document.getElementById('copy-assets-modal___BV_modal_outer_');
          const modalBackdrop = document.getElementById('copy-assets-modal___BV_modal_backdrop_');
          const modal = document.getElementById('copy-assets-modal');

          if (modalOuter) {
            modalOuter.style.zIndex = '10060';
          }
          if (modalBackdrop) {
            modalBackdrop.style.zIndex = '10050';
          }
          if (modal) {
            modal.style.zIndex = '10060';
          }

          const modalElements = document.querySelectorAll('.modal[id*="copy-assets-modal"]');
          modalElements.forEach(element => {
            element.style.zIndex = '10060';
          });
        });
      }
    };

    this.$root.$on('bv::modal::show', this.handleModalZIndex);
  },

  beforeDestroy() {
    saveSelectionAsEventBroker.off(SAVE_SELECTION_EVENTS.SAVE_PROCESSED_IMAGE_TO_FILE_BROWSER, this.initializeSaveMode);
    backgroundThemeHandlerEventBroker.off(COPY_TO_FILE_BROWSER, this.initializeThemeCopyMode);
    videoEventBroker.off(VIDEO_EVENTS.SHOW_FOLDERS_MODAL, this.initializeVideoSaveMode);

    this.$root.$off('bv::modal::show', this.handleModalZIndex);
  },

  methods: {
    handleModalShown() {
      this.currentFolderId = null;
      this.selectedFolder = null;

      if (this.localMode !== 'save') {
        this.localMode = 'copy';
        this.localMediaData = null;
        this.saveFilename = '';
      }
    },


    getModalTitle() {
      if (this.localMode === 'save' && this.currentTab !== TABS.VIDEO) {
        return 'Save Image to Files';
      }
      return this.setTitle(this.currentTab);
    },

    getActionButtonText() {
      return this.localMode === 'save' ? 'Save Here' : 'Copy Here';
    },

    getLoadingText() {
      return this.localMode === 'save' ? 'Saving' : 'Copying';
    },

    isActionDisabled() {
      if (this.localMode === 'save') {
        return this.isImageSaveMode && !this.saveFilename.trim();
      }

      return (this.currentTab !== TABS.FILES && !this.selectedFolder);
    },

    handleAction() {
      if (this.localMode === 'save' && this.currentTab === TABS.VIDEO) {
        return this.handleFolderSelection();
      }

      if (this.localMode === 'save') {
        return this.saveImage();
      }

      return this.copyAssets();
    },

    handleModalHide() {
      this.$bvModal.hide('copy-assets-modal');
      this.$emit('modal-closed');

      if (this.localMode === 'copy' && this.localThemeAsset) {
        $("#selectBkImgModal").modal('hide');
      }
      setTimeout(() => {
        document.body.classList.add('modal-open');
      }, 500);
    },

    onFolderSelected(folder) {
      this.selectedFolder = folder || null;
    },

    getDestinationFolderId() {
      if (this.selectedFolder?.id != null && this.selectedFolder.id !== '') {
        return this.selectedFolder.id;
      }

      const browsedFolderId = this.$refs.destinationView?.internalFolderId;
      if (browsedFolderId != null && browsedFolderId !== '') {
        return browsedFolderId;
      }

      return null;
    },

    async copyAssets() {
      if ((this.currentTab !== TABS.FILES && !this.selectedFolder) || this.isCopying) {
        return;
      }

      this.isCopying = true;
      const totalItems = this.selectedItems.length;
      let successCount = 0;
      let errorCount = 0;
      const failedItems = [];

      try {
        toastr.info(`Starting to copy ${totalItems} item${totalItems > 1 ? 's' : ''}...`);

        for (const [index, item] of this.selectedItems.entries()) {
          try {
            const { data } = await axios.post(this.copyUrl, {
              folder_id: this.selectedFolder ? this.selectedFolder.id : null,
              asset: item
            });

            if (data.status === 'error') {
              toastr.error(data.message);

              errorCount++;
              failedItems.push({
                name: item.name || `Item ${index + 1}`,
                error: data.message || 'Unknown error'
              });
            } else {
              toastr.success(data.message);

              successCount++;
            }
          } catch (itemError) {
            errorCount++;
            const errorMessage = itemError.response?.data?.message || itemError.message || 'Network error occurred';

            failedItems.push({
              name: item.name || `Item ${index + 1}`,
              error: errorMessage
            });
          }
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';

        toastr.error(`Copy operation failed: ${errorMessage}`);
        return;
      } finally {
        this.isCopying = false;
      }

      toastr.info(`Processed ${totalItems} items. <br> ${successCount} items copied successfully. <br> ${errorCount} items failed to copy.`);

      if (successCount > 0) {
        this.handleModalHide();
      }
    },

    async saveImage() {
      if (!this.localMediaData || this.isCopying) {
        return;
      }

      if (!this.saveFilename.trim()) {
        toastr.warning('Filename is required');
        return;
      }

      this.isCopying = true;

      const folderId = this.getDestinationFolderId();
      const filename = this.ensureFilenameHasExtension(this.saveFilename.trim(), this.localMediaData.editedFile.name);
      const editedFile = this.renameFile(this.localMediaData.editedFile, filename);

      try {
        const formData = new FormData();

        if (folderId != null && folderId !== '') {
          formData.append('folder_id', folderId);
        }
        formData.append('filename', filename);
        formData.append('canvas_image', editedFile);

        const { data } = await axios.post('/file/save-canvas-to-files', formData);

        if (data?.status === 'success') {
          toastr.success(data.message || 'Image saved successfully');
          this.handleModalHide();
        } else {
          throw new Error(data?.message || 'Save failed');
        }
      } catch (error) {
        console.error('Save image error:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save image';
        toastr.error(`Save operation failed: ${errorMessage}`);
      } finally {
        this.isCopying = false;
      }
    },

    setCopyUrl(tab) {
      switch (tab) {
        case TABS.BYNDER_ASSETS:
          this.copyUrl = '/bynder/copy-asset';
          break;
        case TABS.PRODUCTS:
        case TABS.THEMES:
          this.copyUrl = '/file/copy-product';
          break;
        case TABS.FILES:
          this.copyUrl = '/file/copy-file';
          break;
        case TABS.SALSIFY_ASSETS:
          this.copyUrl = '/salsify/copy-asset';
          break;
        default:
          this.copyUrl = '/bynder/copy-asset';
          break;
      }
    },

    setTitle(tab) {
      switch (tab) {
        case TABS.BYNDER_ASSETS:
          return 'Copy Bynder Assets to Files';
        case TABS.PRODUCTS:
          return 'Copy Products to Files';
        case TABS.FILES:
          return 'Copy Files to Products';
        case TABS.SALSIFY_ASSETS:
          return 'Copy Salsify Assets to Files';
        case TABS.THEMES:
          return 'Copy Theme to Files';
        case TABS.VIDEO:
          return 'Save Video to Files';
        default:
          return 'Copy Assets to Files';
      }
    },

    initializeSaveMode({ payload }) {
      this.localMode = 'save';
      this.localMediaData = payload;
      this.localThemeAsset = null;
      this.currentTab = TABS.FILES;
      this.setCopyUrl(TABS.FILES);
      this.saveFilename = this.normalizeInitialSaveFilename(payload);

      this.$bvModal.show('copy-assets-modal');
    },

    initializeThemeCopyMode({ asset }) {
      this.localMode = 'copy';
      this.localThemeAsset = asset;
      this.localMediaData = null;
      this.currentTab = TABS.THEMES;
      this.setCopyUrl(TABS.THEMES);

      this.$bvModal.show('copy-assets-modal');
    },

    initializeVideoSaveMode() {
      this.localMode = 'save';

      this.currentTab = TABS.VIDEO;

      this.$bvModal.show('copy-assets-modal');
    },

    handleFolderSelection() {
      videoEventBroker.fire(VIDEO_EVENTS.FOLDER_SELECTED, { folderId: this.selectedFolder.id });

      this.$bvModal.hide('copy-assets-modal');
    },

    normalizeInitialSaveFilename(payload) {
      return payload?.saveFilename || payload?.fileName || payload?.editedFile?.name || 'image.png';
    },

    ensureFilenameHasExtension(filename, fallbackFilename) {
      const filenameWithExtension = filename.match(/\.[a-z0-9]+$/i) ? filename : null;
      const extension = fallbackFilename?.match(/\.([a-z0-9]+)$/i)?.[1] || 'png';

      return this.sanitizeFilename(filenameWithExtension || `${filename}.${extension}`, extension);
    },

    sanitizeFilename(filename, fallbackExtension = 'png') {
      const extensionMatch = filename.match(/\.([a-z0-9]+)$/i);
      const extension = this.normalizeImageExtension(extensionMatch ? extensionMatch[1] : fallbackExtension);
      const rawBaseName = extensionMatch ? filename.slice(0, -extensionMatch[0].length) : filename;
      const baseName = [...rawBaseName]
        .map(char => {
          const code = char.charCodeAt(0);
          const isAlphaNumeric = code >= 48 && code <= 57 || code >= 65 && code <= 90 || code >= 97 && code <= 122;
          const isAllowedSymbol = char === '-' || char === '_' || char === '.';

          return isAlphaNumeric || isAllowedSymbol ? char : '_';
        })
        .join('')
        .replace(/_+/g, '_')
        .replace(/^\.+/, '')
        .replace(/^[._-]+|[._-]+$/g, '') || 'image';
      const maxBaseNameLength = 255 - extension.length - 1;

      return `${baseName.slice(0, maxBaseNameLength)}.${extension}`;
    },

    normalizeImageExtension(extension) {
      const normalizedExtension = (extension || 'png').toLowerCase();

      return ['png', 'jpg', 'jpeg', 'webp'].includes(normalizedExtension) ? normalizedExtension : 'png';
    },

    renameFile(file, filename) {
      if (file.name === filename) {
        return file;
      }

      return new File([file], filename, {
        type: file.type,
        lastModified: file.lastModified,
      });
    }
  }
}
</script>

<style scoped lang="scss">
.copy-assets-modal__filename-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: "Nunito", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #464646;
}
</style>

<style lang="scss">
#copy-assets-modal.select-folder-modal {
  .modal-dialog {
    max-width: 960px;
    margin: 1rem auto;
  }

  .modal-content {
    max-height: calc(100vh - 2rem);
    display: flex;
  }

  .copy-assets-modal__body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }
}

#copy-assets-modal:not(.select-folder-modal) {
  .modal-content {
    height: 100vh;
    width: 85vw;
    max-width: none;
  }

  .modal-body {
    padding: 0;
  }
}
</style>

<style>
.copy-assets-modal,
#copy-assets-modal,
#copy-assets-modal___BV_modal_outer_,
.modal[id*="copy-assets-modal"] {
  z-index: 10060 !important;
}

#copy-assets-modal___BV_modal_backdrop_ {
  z-index: 10050 !important;
}

.modal-open .copy-assets-modal {
  z-index: 10060 !important;
}
</style>
